/**
 * Deeply merges two objects or arrays. The source object will overwrite the target object.
 * If a property in the source object is null, it will overwrite the target property with null.
 *
 * @param target - The target object or array to merge into.
 * @param source - The source object or array to merge from.
 * @returns A new object or array that is the result of merging the target and source.
 */
export function deepMerge<T, U>(target: T, source: U): T & U {
  if (Array.isArray(target) && Array.isArray(source)) {
    const maxLength = Math.max(target.length, source.length);

    if (target.length !== source.length) {
      return source as T & U;
    }

    const result = new Array(maxLength);

    for (let i = 0; i < maxLength; i++) {
      const t = target[i];
      const s = source[i];

      // If the new value is null, set the value to null
      if (s === null) {
        result[i] = null;
        continue;
      }

      // If the old value is undefined, set the value to the new value
      if (t === undefined) {
        result[i] = s;
        continue;
      }

      // If both values are objects or arrays, merge them recursively
      result[i] = deepMerge(t, s);
    }

    return result as any;
  }

  if (isPlainObject(target) && isPlainObject(source)) {
    const result: Record<string, unknown> = { ...target };

    // Check values in new object
    for (const key of Object.keys(source)) {
      const s = source[key];

      // If the new value is undefined, keep the old value
      if (s === undefined) {
        continue;
      }

      const t = target[key];

      // If the new value is null, set the value to null
      if (s === null) {
        result[key] = null;
        continue;
      }

      result[key] = deepMerge(t, s);
    }

    return result as T & U;
  }

  if (source === undefined) {
    return target as T & U;
  }

  return source as T & U;
}

function isPlainObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
